import { useMemo, useState } from 'react';
import { LoaderCircle, Receipt, ShoppingCart } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/api/client';
import { useCreateSale } from '@/api/hooks/useCreateSale';
import { useStoreSettings } from '@/api/hooks/useSettings';
import type { PaymentMode, Sale } from '@/api/types';
import { Can } from '@/auth/guards';
import { PERMISSIONS } from '@/auth/permissions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { BillReceiptDialog } from '@/features/billing/BillReceiptDialog';
import { CartLineRow } from '@/features/billing/CartLineRow';
import { cartActions, useCart } from '@/features/billing/cartStore';
import { computeBillTotals } from '@/features/billing/pricing';
import { money } from '@/lib/utils';

const PAYMENT_MODES: PaymentMode[] = ['Cash', 'Upi', 'Card', 'Credit', 'Online'];
/** Faithful legacy translations where they exist — 'Online' postdates the legacy app. */
const PAYMENT_MODE_KEY: Partial<Record<PaymentMode, string>> = {
  Cash: 'cash',
  Upi: 'upi',
  Card: 'card',
  Credit: 'credit',
};

export function CartPanel() {
  const { t } = useTranslation();
  const cart = useCart();
  const { data: storeSettings } = useStoreSettings();
  const createSale = useCreateSale();

  const [discountInput, setDiscountInput] = useState('');
  const [completedSale, setCompletedSale] = useState<Sale | null>(null);

  const roundOffEnabled = storeSettings?.roundOffBills ?? true;
  const requirePhone = storeSettings?.requireCustomerPhone ?? false;

  const totals = useMemo(
    () => computeBillTotals(cart.items, cart.discount, roundOffEnabled),
    [cart.items, cart.discount, roundOffEnabled],
  );

  const phoneMissing = requirePhone && cart.customerPhone.trim().length === 0;
  const canCheckout = cart.items.length > 0 && !phoneMissing;

  async function handleCheckout() {
    try {
      const sale = await createSale.mutateAsync({
        items: cart.items.map((line) => ({ medicineId: line.medicineId, quantity: line.qty })),
        customerName: cart.customerName.trim() || undefined,
        customerPhone: cart.customerPhone.trim() || undefined,
        paymentMode: cart.paymentMode,
        discount: totals.discount,
      });

      setCompletedSale(sale);
      cartActions.clear();
      setDiscountInput('');
    } catch (error) {
      if (error instanceof ApiError) {
        // These three are the ones a pharmacist can act on immediately —
        // everything else is a generic failure toast.
        if (error.code === 'INSUFFICIENT_STOCK' || error.code === 'EXPIRED_MEDICINE') {
          toast({ title: 'Cannot complete bill', description: error.message, variant: 'destructive' });
        } else if (error.code === 'PHONE_REQUIRED') {
          toast({ title: 'Customer phone required', description: error.message, variant: 'destructive' });
        } else {
          toast({ title: 'Checkout failed', description: error.message, variant: 'destructive' });
        }
      }
    }
  }

  if (cart.items.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center">
        <ShoppingCart className="h-10 w-10 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">Search a medicine to start a bill.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 divide-y divide-border overflow-y-auto px-4">
        {cart.items.map((line) => (
          <CartLineRow key={line.medicineId} line={line} />
        ))}
      </div>

      <div className="space-y-3 border-t border-border p-4">
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label htmlFor="customer-name" className="text-xs">
              {t('customer')} name
            </Label>
            <Input
              id="customer-name"
              value={cart.customerName}
              onChange={(e) => cartActions.setCustomer(e.target.value, cart.customerPhone)}
              placeholder="Walk-in"
              className="h-9"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="customer-phone" className="text-xs">
              Phone {requirePhone && <span className="text-status-expired">*</span>}
            </Label>
            <Input
              id="customer-phone"
              value={cart.customerPhone}
              onChange={(e) => cartActions.setCustomer(cart.customerName, e.target.value)}
              placeholder="10-digit number"
              className="h-9"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Can permission={PERMISSIONS.salesDiscount}>
            <div className="space-y-1">
              <Label htmlFor="discount" className="text-xs">
                {t('discount')} (₹)
              </Label>
              <Input
                id="discount"
                inputMode="decimal"
                value={discountInput}
                onChange={(e) => {
                  setDiscountInput(e.target.value);
                  const value = Number(e.target.value);
                  cartActions.setDiscount(Number.isFinite(value) ? value : 0);
                }}
                placeholder="0.00"
                className="h-9"
              />
            </div>
          </Can>
          <div className="space-y-1">
            <Label className="text-xs">{t('paymentMode')}</Label>
            <Select
              value={cart.paymentMode}
              onValueChange={(value) => cartActions.setPaymentMode(value as PaymentMode)}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_MODES.map((mode) => (
                  <SelectItem key={mode} value={mode}>
                    {PAYMENT_MODE_KEY[mode] ? t(PAYMENT_MODE_KEY[mode]!) : mode}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1 rounded-lg bg-secondary/50 p-3 text-sm">
          <div className="flex justify-between text-muted-foreground">
            <span>Subtotal</span>
            <span className="tabular">{money(totals.subtotal)}</span>
          </div>
          {totals.discount > 0 && (
            <div className="flex justify-between text-muted-foreground">
              <span>{t('discount')}</span>
              <span className="tabular">−{money(totals.discount)}</span>
            </div>
          )}
          {totals.gstBreakdown.map((entry) => (
            <div key={entry.rate} className="flex justify-between text-muted-foreground">
              <span>GST {entry.rate}%</span>
              <span className="tabular">{money(entry.amount)}</span>
            </div>
          ))}
          {totals.roundOff !== 0 && (
            <div className="flex justify-between text-muted-foreground">
              <span>Round off</span>
              <span className="tabular">{totals.roundOff > 0 ? '+' : ''}{money(totals.roundOff)}</span>
            </div>
          )}
          <div className="flex justify-between border-t border-border pt-1.5 text-base font-semibold">
            <span>{t('total')}</span>
            <span className="tabular">{money(totals.grandTotal)}</span>
          </div>
        </div>

        {phoneMissing && (
          <p className="text-xs text-status-expired">Customer phone is required for every bill.</p>
        )}

        <Button
          size="touch"
          className="w-full"
          disabled={!canCheckout || createSale.isPending}
          onClick={handleCheckout}
        >
          {createSale.isPending ? <LoaderCircle className="animate-spin" /> : <Receipt />}
          {createSale.isPending ? 'Generating bill…' : `Charge ${money(totals.grandTotal)}`}
        </Button>
      </div>

      <BillReceiptDialog
        sale={completedSale}
        onOpenChange={(open) => !open && setCompletedSale(null)}
        onNewBill={() => setCompletedSale(null)}
      />
    </div>
  );
}